import type { ResolvedBrandFont } from "@ascleo/frontdesk-contracts/branding/model";
import type { PublishedBrandingContent } from "@ascleo/frontdesk-contracts/branding/public";
import type { BrandingPracticeStyle } from "./branding-types";

const SCRIPT_FALLBACK =
  '"Noto Sans", "Noto Sans Arabic", "Noto Sans CJK SC", "Helvetica Neue", Arial, system-ui, sans-serif';

/**
 * Resolve a server-selected font to an internal family and fixed multilingual fallback stack.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { safeBrandFontFamily } from "@ascleo/frontdesk-shared/branding";
 *
 * safeBrandFontFamily({ source: "builtin", familyId: "geist-variable", cssFamily: "ignored", fallback: "ignored", display: "swap", faces: [{ weightMin: 100, weightMax: 900, style: "normal" }] });
 * ```
 */
export function safeBrandFontFamily(font: ResolvedBrandFont): string {
  const family =
    font.source === "builtin"
      ? font.familyId === "ryker"
        ? '"Ryker"'
        : font.familyId === "neue-haas-display"
          ? '"Neue Haas Display"'
          : font.familyId === "fraunces-variable"
        ? '"Fraunces Variable"'
        : '"Geist Variable"'
      : `"FrontDesk Managed ${font.familyId.slice(0, 12)}"`;
  return `${family}, ${SCRIPT_FALLBACK}`;
}

/**
 * Build the immutable fixed-key style object accepted by the patient brand scope.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { practiceStyleFromContent } from "@ascleo/frontdesk-shared/branding";
 *
 * practiceStyleFromContent({ kind: "default" }); // {}
 * ```
 */
export function practiceStyleFromContent(
  content: PublishedBrandingContent,
): BrandingPracticeStyle {
  if (content.kind === "default") return Object.freeze({});
  const { primary, accent, supporting, interactionAccent } = content.colorTokens;
  return Object.freeze({
    "--primary": primary.base,
    "--primary-foreground": primary.foreground,
    "--primary-neon": primary.neon,
    "--primary-soft": primary.soft,
    "--primary-subtle": primary.subtle,
    "--primary-vivid": primary.vivid,
    "--primary-ink": primary.ink,
    "--brand-accent": accent.base,
    "--brand-accent-foreground": accent.foreground,
    "--brand-accent-neon": accent.neon,
    "--brand-accent-soft": accent.soft,
    "--brand-accent-subtle": accent.subtle,
    "--brand-accent-vivid": accent.vivid,
    "--brand-accent-ink": accent.ink,
    "--brand-supporting": supporting.base,
    "--brand-supporting-foreground": supporting.foreground,
    "--brand-supporting-neon": supporting.neon,
    "--brand-supporting-soft": supporting.soft,
    "--brand-supporting-subtle": supporting.subtle,
    "--brand-supporting-vivid": supporting.vivid,
    "--brand-supporting-ink": supporting.ink,
    "--accent": interactionAccent.base,
    "--accent-foreground": interactionAccent.foreground,
    "--font-sans": safeBrandFontFamily(content.fonts.body),
    "--font-heading": safeBrandFontFamily(content.fonts.heading),
  });
}
